import React from 'react';
import { DifficultyCategory } from './types';
import { DIFFICULTY_RAMP } from './dataViz';

/**
 * 6love DifficultyBadge — a strength-of-schedule difficulty category rendered as
 * a colored pill on the canonical easier→harder ramp. The product used to
 * hard-code this five-step scale (labels + colors) inside
 * StrengthOfScheduleChart; this is the shared version for legends, tooltips, and
 * leaderboards. Optionally appends the signed rating delta, e.g. "Harder +0.42".
 *
 * App implementation of the design system's `DifficultyBadge`.
 */

/** Label + ramp color for each difficulty step — the single source of truth. */
export const DIFFICULTY_META: Record<
  DifficultyCategory,
  { label: string; color: string }
> = {
  'much-easier': { label: 'Much easier', color: DIFFICULTY_RAMP['much-easier'] },
  easier: { label: 'Easier', color: DIFFICULTY_RAMP.easier },
  even: { label: 'Even', color: DIFFICULTY_RAMP.even },
  harder: { label: 'Harder', color: DIFFICULTY_RAMP.harder },
  'much-harder': { label: 'Much harder', color: DIFFICULTY_RAMP['much-harder'] },
};

interface DifficultyBadgeProps {
  /** Which step of the difficulty ramp. */
  category: DifficultyCategory;
  /** Optional rating delta (opponent − team); appended as " +0.42". */
  diff?: number | null;
  /**
   * `"soft"` (default) tinted pill, `"solid"` filled ramp color,
   * `"dot"` label with a leading color dot and no background.
   */
  variant?: 'soft' | 'solid' | 'dot';
  className?: string;
}

const DifficultyBadge: React.FC<DifficultyBadgeProps> = ({
  category,
  diff = null,
  variant = 'soft',
  className,
}) => {
  const meta = DIFFICULTY_META[category] ?? DIFFICULTY_META.even;
  const sign = diff != null && diff >= 0 ? '+' : '';
  const deltaLabel = diff != null ? ` ${sign}${diff.toFixed(2)}` : '';
  const label = `${meta.label}${deltaLabel}`;

  if (variant === 'dot') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap${
          className ? ` ${className}` : ''
        }`}
      >
        <span
          className="inline-block w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: meta.color }}
        />
        {label}
      </span>
    );
  }

  if (variant === 'solid') {
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold text-white whitespace-nowrap${
          className ? ` ${className}` : ''
        }`}
        style={{ backgroundColor: meta.color }}
      >
        {label}
      </span>
    );
  }

  // soft (default): tinted background, ramp color as text + leading dot.
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap${
        className ? ` ${className}` : ''
      }`}
      style={{
        color: meta.color,
        backgroundColor: `color-mix(in srgb, ${meta.color} 14%, transparent)`,
      }}
    >
      <span
        className="inline-block w-[7px] h-[7px] rounded-full shrink-0"
        style={{ backgroundColor: meta.color }}
      />
      {label}
    </span>
  );
};

export default DifficultyBadge;
